import React from 'react';
import CoverageGauge from './CoverageGauge';
import GapTable from './GapTable';
import ProductCards from './ProductCards';
import ActionChips from './ActionChips';
import ComparisonMatrix from './ComparisonMatrix';
import DocSnippets from './DocSnippets';
import CapabilityTree from './CapabilityTree';
import DynamicRenderer from './primitives/DynamicRenderer';
import DrillableValue from './primitives/DrillableValue';
import './ChatComponents.css';

// Currency display that stays honest below $1M — a $50K row must never
// render as "$0.0M" (reads as zero/data-quality issue).
function formatSpend(value) {
  const n = Number(value) || 0;
  if (Math.abs(n) >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (Math.abs(n) >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (Math.abs(n) >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${n.toLocaleString()}`;
}

/**
 * Component registry mapping type names to React components
 */
const COMPONENT_MAP = {
  // Framework analysis
  coverage_gauge: CoverageGauge,
  gap_table: GapTable,
  controls_table: GapTable, // Reuse GapTable for controls

  // Products & capabilities
  product_cards: ProductCards,
  product_grid: ProductCards, // Alias
  capability_tree: CapabilityTree,
  capability_card: CapabilityCard,
  config_card: ConfigCard,
  steps_timeline: StepsTimeline,

  // Competitive
  comparison_matrix: ComparisonMatrix,
  win_loss_summary: WinLossSummary,

  // Documentation
  doc_snippets: DocSnippets,
  search_info: SearchInfo,

  // Procurement
  procurement_table: ProcurementTable,
  results_summary: ResultsSummary,
  metrics_grid: MetricsGrid,
  department_table: DepartmentTable,
  spend_table: SpendTable,
  spend_sankey: SpendSankey,
  methods_chart: MethodsChart,

  // Task Management
  task_list: TaskList,
  task_created: TaskCreated,
  task_reminders: TaskReminders,
  task_board: () => null, // Full board rendered by TaskCanvas
  task_calendar: () => null, // Full calendar rendered by TaskCanvas

  // Intelligence
  account_card: AccountCard,
  intel_list: IntelList,
  briefing_card: BriefingCard,
  intel_feed: IntelFeed,
  empty_state: EmptyState,

  // Generative UI - AI composes primitives on the fly
  dynamic: DynamicComponent,

  // Common
  error_card: ErrorCard,
};

/**
 * Main renderer that maps UI component data to React components
 */
function ChatComponentRenderer({ components, suggestedActions, onAction }) {
  console.log('[ChatComponentRenderer] Rendering:', {
    componentCount: components?.length,
    types: components?.map(c => c.type),
    components
  });

  if (!components || components.length === 0) {
    return suggestedActions?.length > 0 ? (
      <ActionChips actions={suggestedActions} onAction={onAction} />
    ) : null;
  }

  return (
    <div className="cc-renderer">
      {components.map((component, idx) => {
        const Component = COMPONENT_MAP[component.type];

        if (!Component) {
          console.warn(`[ChatComponentRenderer] Unknown component type: ${component.type}`, component);
          return null;
        }

        console.log(`[ChatComponentRenderer] Rendering ${component.type}:`, { data: component.data, title: component.title });

        return (
          <div key={idx} className="cc-component-wrapper">
            <Component
              title={component.title}
              data={component.data}
              actions={component.actions}
              collapsible={component.collapsible}
              defaultCollapsed={component.defaultCollapsed}
              onAction={onAction}
            />
          </div>
        );
      })}

      {suggestedActions?.length > 0 && (
        <ActionChips actions={suggestedActions} onAction={onAction} />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Simple inline components
// ─────────────────────────────────────────────────────────────────────

function CapabilityCard({ data }) {
  return (
    <div className="cc-capability-card" style={{
      background: 'var(--color-surface)',
      borderRadius: 'var(--radius-lg)',
      border: '1px solid var(--color-border)',
      padding: 'var(--space-3)',
      marginBottom: 'var(--space-2)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
        <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)' }}>
          {data.name}
        </span>
        {data.code && (
          <span style={{
            fontSize: '10px',
            fontFamily: 'monospace',
            padding: '2px 6px',
            background: 'rgba(var(--color-primary-rgb), 0.1)',
            color: 'var(--color-primary)',
            borderRadius: 'var(--radius-sm)',
          }}>
            {data.code}
          </span>
        )}
      </div>
      {data.description && (
        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', margin: 0 }}>
          {data.description}
        </p>
      )}
      {data.businessValue && (
        <p style={{
          fontSize: 'var(--text-xs)',
          color: 'var(--color-success)',
          marginTop: 'var(--space-2)',
          fontStyle: 'italic',
        }}>
          {data.businessValue}
        </p>
      )}
    </div>
  );
}

function ConfigCard({ data }) {
  const { capability, coverage, license, setup, dependencies } = data;

  return (
    <div style={{
      background: 'var(--color-surface)',
      borderRadius: 'var(--radius-lg)',
      border: '1px solid var(--color-border)',
      padding: 'var(--space-3)',
    }}>
      <div style={{ marginBottom: 'var(--space-3)' }}>
        <div style={{ fontWeight: 'var(--font-semibold)', fontSize: 'var(--text-sm)' }}>
          {capability?.name}
        </div>
        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
          {capability?.pillar} &gt; {capability?.domain}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 'var(--space-2)' }}>
        <ConfigItem label="Coverage" value={coverage?.level} />
        <ConfigItem label="License" value={license?.tier} badge={license?.is_addon ? 'Add-on' : null} />
        <ConfigItem label="Setup" value={setup?.complexity} />
        <ConfigItem label="Time" value={setup?.time_hours ? `~${setup.time_hours}h` : 'N/A'} />
      </div>

      {dependencies?.requires_products?.length > 0 && (
        <div style={{ marginTop: 'var(--space-2)', fontSize: 'var(--text-xs)' }}>
          <strong>Requires:</strong> {dependencies.requires_products.join(', ')}
        </div>
      )}
    </div>
  );
}

function ConfigItem({ label, value, badge }) {
  return (
    <div style={{ fontSize: 'var(--text-xs)' }}>
      <span style={{ color: 'var(--color-text-muted)' }}>{label}:</span>{' '}
      <span style={{ fontWeight: 'var(--font-medium)' }}>{value || 'N/A'}</span>
      {badge && (
        <span style={{
          marginLeft: '4px',
          padding: '1px 4px',
          fontSize: '9px',
          background: 'var(--color-warning)',
          color: 'white',
          borderRadius: 'var(--radius-sm)',
        }}>
          {badge}
        </span>
      )}
    </div>
  );
}

function StepsTimeline({ title, data }) {
  return (
    <div style={{ marginTop: 'var(--space-3)' }}>
      {title && <div className="cc-section-title">{title}</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
        {data.steps?.map((step, idx) => (
          <div key={idx} style={{
            display: 'flex',
            gap: 'var(--space-2)',
            padding: 'var(--space-2)',
            background: 'var(--color-surface)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--color-border)',
          }}>
            <span style={{
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              background: 'var(--color-primary)',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 'var(--text-xs)',
              fontWeight: 'var(--font-bold)',
              flexShrink: 0,
            }}>
              {step.order}
            </span>
            <div>
              <div style={{ fontWeight: 'var(--font-medium)', fontSize: 'var(--text-xs)' }}>
                {step.title}
              </div>
              {step.description && (
                <div style={{ fontSize: '10px', color: 'var(--color-text-muted)' }}>
                  {step.description}
                </div>
              )}
              {step.estimatedMinutes && (
                <span style={{ fontSize: '10px', color: 'var(--color-text-muted)' }}>
                  ~{step.estimatedMinutes} min
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function WinLossSummary({ data }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: 'var(--space-2)',
      marginTop: 'var(--space-2)',
    }}>
      <div style={{
        padding: 'var(--space-2)',
        background: 'rgba(var(--color-success-rgb), 0.1)',
        borderRadius: 'var(--radius-md)',
        textAlign: 'center',
      }}>
        <div style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-bold)', color: 'var(--color-success)' }}>
          {data.wins?.length || 0}
        </div>
        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>PA Stronger</div>
      </div>
      <div style={{
        padding: 'var(--space-2)',
        background: 'rgba(var(--color-warning-rgb), 0.1)',
        borderRadius: 'var(--radius-md)',
        textAlign: 'center',
      }}>
        <div style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-bold)', color: 'var(--color-warning)' }}>
          {data.ties || 0}
        </div>
        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>Equivalent</div>
      </div>
      <div style={{
        padding: 'var(--space-2)',
        background: 'rgba(var(--color-error-rgb), 0.1)',
        borderRadius: 'var(--radius-md)',
        textAlign: 'center',
      }}>
        <div style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-bold)', color: 'var(--color-error)' }}>
          {data.losses?.length || 0}
        </div>
        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>Competitor Stronger</div>
      </div>
    </div>
  );
}

function SearchInfo({ data }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 'var(--space-2)',
      padding: 'var(--space-2)',
      background: 'rgba(var(--color-primary-rgb), 0.05)',
      borderRadius: 'var(--radius-md)',
      fontSize: 'var(--text-xs)',
      marginBottom: 'var(--space-2)',
    }}>
      <span>Query: <strong>{data.query}</strong></span>
      <span style={{ color: 'var(--color-text-muted)' }}>|</span>
      <span>Mode: {data.mode}</span>
      <span style={{ color: 'var(--color-text-muted)' }}>|</span>
      <span>{data.resultCount} results</span>
    </div>
  );
}

function ProcurementTable({ title, data, onAction }) {
  return (
    <div className="cc-table-container">
      {title && <div className="cc-table-title">{title}</div>}
      <div className="cc-table-scroll">
        <table className="cc-table">
          <thead>
            <tr>
              <th>Description</th>
              <th>Supplier</th>
              <th>Amount</th>
              <th>Classification</th>
            </tr>
          </thead>
          <tbody>
            {data.rows?.map((row, idx) => (
              <tr key={idx}>
                <td>
                  <div style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {row.description}
                  </div>
                </td>
                <td>
                  <DrillableValue drill={row.supplier && { nodeType: 'supplier', nodeValue: row.supplier, department: row.department }}>
                    {row.supplier}
                  </DrillableValue>
                </td>
                <td style={{ textAlign: 'right' }}>
                  <DrillableValue drill={row.amount && row.supplier && { nodeType: 'supplier', nodeValue: row.supplier, department: row.department }}>
                    {row.amount ? `$${Number(row.amount).toLocaleString()}` : '-'}
                  </DrillableValue>
                </td>
                <td>
                  <DrillableValue drill={row.component && { nodeType: 'component', nodeValue: row.component }}>
                    {row.component || <span style={{ color: 'var(--color-warning)' }}>Unclassified</span>}
                  </DrillableValue>
                  {row.confidence && (
                    <span style={{ fontSize: '10px', marginLeft: '4px', color: 'var(--color-text-muted)' }}>
                      ({(row.confidence * 100).toFixed(0)}%)
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ResultsSummary({ data }) {
  // Handle missing or empty data
  if (!data) {
    return null;
  }

  const totalMatches = data.totalMatches ?? data.total ?? data.count ?? 0;
  const showing = data.showing ?? data.displayed ?? totalMatches;
  const query = data.query || data.searchTerm || '';

  // Empty results state
  if (totalMatches === 0) {
    return (
      <div style={{
        padding: 'var(--space-3)',
        background: 'rgba(var(--color-warning-rgb), 0.08)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid rgba(var(--color-warning-rgb), 0.2)',
        fontSize: 'var(--text-sm)',
        marginBottom: 'var(--space-2)',
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
      }}>
        <span style={{ fontSize: '16px' }}>🔍</span>
        <span>
          {query ? (
            <>No results found for "<strong>{query}</strong>"</>
          ) : (
            'No results found'
          )}
        </span>
      </div>
    );
  }

  // Results found state
  return (
    <div style={{
      padding: 'var(--space-2) var(--space-3)',
      background: 'rgba(var(--color-success-rgb), 0.08)',
      borderRadius: 'var(--radius-md)',
      border: '1px solid rgba(var(--color-success-rgb), 0.2)',
      fontSize: 'var(--text-sm)',
      marginBottom: 'var(--space-2)',
      display: 'flex',
      alignItems: 'center',
      gap: 'var(--space-2)',
    }}>
      <span style={{ fontSize: '16px' }}>✓</span>
      <span>
        Found <strong style={{ color: 'var(--color-success)' }}>{totalMatches.toLocaleString()}</strong> {totalMatches === 1 ? 'item' : 'items'}
        {query && <> matching "<strong>{query}</strong>"</>}
        {showing < totalMatches && (
          <span style={{ color: 'var(--color-text-muted)' }}> (showing {showing.toLocaleString()})</span>
        )}
      </span>
    </div>
  );
}

function MetricsGrid({ data }) {
  const getColor = (color) => {
    switch (color) {
      case 'green': return 'var(--color-success)';
      case 'amber': return 'var(--color-warning)';
      case 'red': return 'var(--color-error)';
      default: return 'var(--color-text)';
    }
  };

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
      gap: 'var(--space-2)',
      marginBottom: 'var(--space-3)',
    }}>
      {data.metrics?.map((metric, idx) => (
        <div key={idx} style={{
          padding: 'var(--space-3)',
          background: 'var(--color-surface)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--color-border)',
          textAlign: 'center',
        }}>
          <div style={{
            fontSize: 'var(--text-xl)',
            fontWeight: 'var(--font-bold)',
            color: getColor(metric.color),
          }}>
            <DrillableValue drill={metric.drill}>
              {metric.format === 'number' ? (Number(metric.value) || 0).toLocaleString() : metric.value}
            </DrillableValue>
          </div>
          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
            {metric.label}
          </div>
        </div>
      ))}
    </div>
  );
}

function DepartmentTable({ title, data }) {
  return (
    <div className="cc-table-container">
      {title && <div className="cc-table-title">{title}</div>}
      <div className="cc-table-scroll">
        <table className="cc-table">
          <thead>
            <tr>
              <th>Department</th>
              <th style={{ textAlign: 'right' }}>Items</th>
              <th style={{ textAlign: 'right' }}>Rate</th>
              <th style={{ textAlign: 'right' }}>Spend</th>
            </tr>
          </thead>
          <tbody>
            {data.rows?.map((row, idx) => (
              <tr key={idx}>
                <td>{row.department}</td>
                <td style={{ textAlign: 'right' }}>{row.totalItems?.toLocaleString()}</td>
                <td style={{ textAlign: 'right' }}>{row.classificationPct != null ? `${row.classificationPct}%` : '—'}</td>
                <td style={{ textAlign: 'right' }}>
                  {formatSpend(row.totalSpend)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ErrorCard({ data }) {
  return (
    <div className="cc-error-card">
      <div className="cc-error-message">{data.message}</div>
      {data.hint && <div className="cc-error-hint">{data.hint}</div>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// TASK MANAGEMENT COMPONENTS
// ─────────────────────────────────────────────────────────────────────

function TaskList({ title, data }) {
  const { urgent = [], high = [], other = [], counts = {}, total = 0 } = data;
  const allTasks = [...urgent, ...high, ...other];

  if (allTasks.length === 0) {
    return <EmptyState data={{ message: 'No tasks found', icon: 'check-circle' }} />;
  }

  return (
    <div style={{ padding: 'var(--space-2)' }}>
      {title && <div className="cc-section-title">{title}</div>}

      {/* Counts summary */}
      {counts && (
        <div style={{
          display: 'flex',
          gap: 'var(--space-2)',
          marginBottom: 'var(--space-3)',
          flexWrap: 'wrap',
        }}>
          {counts.overdue > 0 && <TaskCountBadge label="Overdue" count={counts.overdue} color="error" />}
          {counts.due_today > 0 && <TaskCountBadge label="Due Today" count={counts.due_today} color="warning" />}
          {counts.active > 0 && <TaskCountBadge label="Active" count={counts.active} color="primary" />}
        </div>
      )}

      {/* Urgent tasks */}
      {urgent.length > 0 && (
        <TaskSection label="🔴 Urgent" tasks={urgent} borderColor="var(--color-error)" />
      )}

      {/* High priority tasks */}
      {high.length > 0 && (
        <TaskSection label="🟠 High Priority" tasks={high} borderColor="var(--color-warning)" />
      )}

      {/* Other tasks */}
      {other.length > 0 && (
        <TaskSection label="📋 Other Tasks" tasks={other} borderColor="var(--color-border)" />
      )}
    </div>
  );
}

function TaskSection({ label, tasks, borderColor }) {
  return (
    <div style={{ marginBottom: 'var(--space-3)' }}>
      <div style={{
        fontSize: 'var(--text-xs)',
        fontWeight: 'var(--font-semibold)',
        marginBottom: 'var(--space-1)',
        color: 'var(--color-text-muted)',
      }}>
        {label}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
        {tasks.map((task, idx) => (
          <TaskRow key={task.id || idx} task={task} borderColor={borderColor} />
        ))}
      </div>
    </div>
  );
}

function TaskRow({ task, borderColor }) {
  const priorityColors = {
    urgent: 'var(--color-error)',
    high: 'var(--color-warning)',
    medium: 'var(--color-primary)',
    low: 'var(--color-text-muted)',
  };

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 'var(--space-2)',
      padding: 'var(--space-2)',
      background: 'var(--color-surface)',
      borderRadius: 'var(--radius-md)',
      borderLeft: `3px solid ${borderColor}`,
    }}>
      <div style={{ flex: 1 }}>
        <div style={{
          fontSize: 'var(--text-sm)',
          fontWeight: 'var(--font-medium)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-1)',
        }}>
          {task.title}
          {task.is_overdue && (
            <span style={{
              fontSize: '10px',
              padding: '1px 4px',
              background: 'var(--color-error)',
              color: 'white',
              borderRadius: 'var(--radius-sm)',
            }}>
              {task.days_overdue ? `${task.days_overdue}d overdue` : 'Overdue'}
            </span>
          )}
        </div>
        <div style={{
          fontSize: 'var(--text-xs)',
          color: 'var(--color-text-muted)',
          display: 'flex',
          gap: 'var(--space-2)',
          marginTop: '2px',
        }}>
          {task.task_type && <span>{formatTaskType(task.task_type)}</span>}
          {task.due_date && <span>Due: {formatDate(task.due_date)}</span>}
        </div>
      </div>
      <div style={{
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        background: priorityColors[task.priority] || priorityColors.medium,
      }} />
    </div>
  );
}

function TaskCountBadge({ label, count, color }) {
  const colors = {
    error: { bg: 'rgba(var(--color-error-rgb), 0.1)', text: 'var(--color-error)' },
    warning: { bg: 'rgba(var(--color-warning-rgb), 0.1)', text: 'var(--color-warning)' },
    primary: { bg: 'rgba(var(--color-primary-rgb), 0.1)', text: 'var(--color-primary)' },
    success: { bg: 'rgba(var(--color-success-rgb), 0.1)', text: 'var(--color-success)' },
  };
  const c = colors[color] || colors.primary;

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 'var(--space-1)',
      padding: '4px 8px',
      background: c.bg,
      borderRadius: 'var(--radius-md)',
      fontSize: 'var(--text-xs)',
    }}>
      <span style={{ fontWeight: 'var(--font-bold)', color: c.text }}>{count}</span>
      <span style={{ color: 'var(--color-text-muted)' }}>{label}</span>
    </div>
  );
}

function TaskCreated({ data }) {
  return (
    <div style={{
      padding: 'var(--space-3)',
      background: 'rgba(var(--color-success-rgb), 0.1)',
      borderRadius: 'var(--radius-lg)',
      border: '1px solid rgba(var(--color-success-rgb), 0.2)',
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        marginBottom: 'var(--space-2)',
      }}>
        <span style={{ fontSize: '18px' }}>✓</span>
        <span style={{ fontWeight: 'var(--font-semibold)', color: 'var(--color-success)' }}>
          Task Created
        </span>
      </div>
      <div style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)' }}>
        {data.title}
      </div>
      {data.due_date && (
        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginTop: '4px' }}>
          Due: {formatDate(data.due_date)}
        </div>
      )}
    </div>
  );
}

function TaskReminders({ title, data }) {
  const { headline, summary, overdue = [], due_today = [], due_tomorrow = [], stale = [] } = data;

  return (
    <div style={{ padding: 'var(--space-2)' }}>
      {title && <div className="cc-section-title">{title}</div>}

      {/* Headline */}
      <div style={{
        padding: 'var(--space-3)',
        background: 'var(--color-surface)',
        borderRadius: 'var(--radius-lg)',
        marginBottom: 'var(--space-3)',
      }}>
        <div style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)' }}>
          {headline}
        </div>
        {summary && (
          <div style={{
            display: 'flex',
            gap: 'var(--space-3)',
            marginTop: 'var(--space-2)',
            fontSize: 'var(--text-xs)',
            color: 'var(--color-text-muted)',
          }}>
            {summary.overdue > 0 && <span>🔴 {summary.overdue} overdue</span>}
            {summary.due_today > 0 && <span>🟡 {summary.due_today} due today</span>}
            {summary.due_tomorrow > 0 && <span>🟢 {summary.due_tomorrow} due tomorrow</span>}
          </div>
        )}
      </div>

      {overdue.length > 0 && <TaskSection label="🔴 Overdue" tasks={overdue} borderColor="var(--color-error)" />}
      {due_today.length > 0 && <TaskSection label="🟡 Due Today" tasks={due_today} borderColor="var(--color-warning)" />}
      {due_tomorrow.length > 0 && <TaskSection label="🟢 Due Tomorrow" tasks={due_tomorrow} borderColor="var(--color-success)" />}
      {stale.length > 0 && (
        <TaskSection
          label="⏳ Stale Tasks (no due date, getting old)"
          tasks={stale}
          borderColor="var(--color-text-muted)"
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// INTELLIGENCE COMPONENTS
// ─────────────────────────────────────────────────────────────────────

function AccountCard({ data }) {
  return (
    <div style={{
      padding: 'var(--space-3)',
      background: 'var(--color-surface)',
      borderRadius: 'var(--radius-lg)',
      border: '1px solid var(--color-border)',
      marginBottom: 'var(--space-2)',
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        marginBottom: 'var(--space-2)',
      }}>
        <span style={{ fontSize: '20px' }}>🏢</span>
        <div>
          <div style={{ fontWeight: 'var(--font-semibold)', fontSize: 'var(--text-sm)' }}>
            {data.name}
          </div>
          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
            {[data.industry, data.tier].filter(Boolean).join(' · ')}
          </div>
        </div>
      </div>
      {data.summary && (
        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
          {data.summary}
        </div>
      )}
    </div>
  );
}

function IntelList({ title, data }) {
  const { sections = [] } = data;

  return (
    <div style={{ padding: 'var(--space-2)' }}>
      {title && <div className="cc-section-title">{title}</div>}
      {sections.map((section, idx) => (
        <div key={idx} style={{ marginBottom: 'var(--space-3)' }}>
          <div style={{
            fontSize: 'var(--text-xs)',
            fontWeight: 'var(--font-semibold)',
            color: 'var(--color-text-muted)',
            marginBottom: 'var(--space-1)',
            textTransform: 'uppercase',
          }}>
            {section.typeLabel}
          </div>
          {section.items.map((item, iidx) => (
            <IntelItem key={iidx} item={item} />
          ))}
        </div>
      ))}
    </div>
  );
}

function IntelItem({ item }) {
  const urgencyColors = {
    critical: 'var(--color-error)',
    high: 'var(--color-warning)',
    medium: 'var(--color-primary)',
    low: 'var(--color-text-muted)',
  };

  return (
    <div style={{
      padding: 'var(--space-2)',
      background: 'var(--color-surface)',
      borderRadius: 'var(--radius-md)',
      marginBottom: 'var(--space-1)',
      borderLeft: `3px solid ${urgencyColors[item.urgency] || urgencyColors.medium}`,
    }}>
      <div style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)' }}>
        {item.title}
      </div>
      {item.summary && (
        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginTop: '4px' }}>
          {item.summary}
        </div>
      )}
    </div>
  );
}

function BriefingCard({ title, data }) {
  const { headline, summary, critical_alerts = [], high_priority = [], suggested_actions = [] } = data;

  return (
    <div style={{ padding: 'var(--space-2)' }}>
      {title && <div className="cc-section-title">{title}</div>}

      <div style={{
        padding: 'var(--space-3)',
        background: 'var(--color-surface)',
        borderRadius: 'var(--radius-lg)',
        marginBottom: 'var(--space-3)',
      }}>
        <div style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)' }}>
          {headline}
        </div>
        {summary && (
          <div style={{
            display: 'flex',
            gap: 'var(--space-3)',
            marginTop: 'var(--space-2)',
            fontSize: 'var(--text-xs)',
            color: 'var(--color-text-muted)',
          }}>
            {summary.critical_count > 0 && <span>🔴 {summary.critical_count} critical</span>}
            {summary.high_priority_count > 0 && <span>🟠 {summary.high_priority_count} high priority</span>}
            {summary.action_item_count > 0 && <span>📋 {summary.action_item_count} action items</span>}
          </div>
        )}
      </div>

      {critical_alerts.length > 0 && (
        <div style={{ marginBottom: 'var(--space-3)' }}>
          <div className="cc-section-subtitle">Critical Alerts</div>
          {critical_alerts.map((item, idx) => (
            <IntelItem key={idx} item={item} />
          ))}
        </div>
      )}

      {high_priority.length > 0 && (
        <div style={{ marginBottom: 'var(--space-3)' }}>
          <div className="cc-section-subtitle">High Priority</div>
          {high_priority.map((item, idx) => (
            <IntelItem key={idx} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}

function IntelFeed({ title, data }) {
  const { items = [], counts, showing } = data;

  return (
    <div style={{ padding: 'var(--space-2)' }}>
      {title && <div className="cc-section-title">{title}</div>}

      {counts && (
        <div style={{
          display: 'flex',
          gap: 'var(--space-2)',
          marginBottom: 'var(--space-3)',
        }}>
          {counts.unread > 0 && <TaskCountBadge label="Unread" count={counts.unread} color="primary" />}
          {counts.saved > 0 && <TaskCountBadge label="Saved" count={counts.saved} color="success" />}
        </div>
      )}

      {items.map((item, idx) => (
        <div key={idx} style={{ marginBottom: 'var(--space-2)' }}>
          <div style={{
            fontSize: '10px',
            color: 'var(--color-text-muted)',
            marginBottom: '2px',
            textTransform: 'uppercase',
          }}>
            {item.typeLabel}
          </div>
          <IntelItem item={item} />
        </div>
      ))}

      {showing && items.length > 0 && (
        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', textAlign: 'center' }}>
          Showing {showing} items
        </div>
      )}
    </div>
  );
}

function EmptyState({ data }) {
  return (
    <div style={{
      padding: 'var(--space-6)',
      textAlign: 'center',
      color: 'var(--color-text-muted)',
    }}>
      <div style={{ fontSize: '24px', marginBottom: 'var(--space-2)' }}>
        {data.icon === 'check-circle' ? '✓' : '📭'}
      </div>
      <div style={{ fontSize: 'var(--text-sm)' }}>{data.message}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// GENERATIVE UI - DYNAMIC COMPONENT
// ─────────────────────────────────────────────────────────────────────

/**
 * DynamicComponent - Wrapper for AI-composed UIs
 *
 * The AI uses render_ui tool to compose custom layouts from primitives.
 * This component passes the schema to DynamicRenderer for interpretation.
 */
function DynamicComponent({ title, data, onAction }) {
  console.log('[DynamicComponent] Rendering generative UI:', {
    title,
    schema: data?.schema,
    generative: data?.generative,
  });

  if (!data?.schema) {
    console.warn('[DynamicComponent] No schema provided');
    return null;
  }

  return (
    <div className="cc-dynamic-wrapper">
      {title && (
        <div
          style={{
            fontSize: 'var(--text-sm)',
            fontWeight: 'var(--font-semibold)',
            marginBottom: 'var(--space-2)',
            color: 'var(--color-text)',
          }}
        >
          {title}
        </div>
      )}
      <DynamicRenderer schema={data.schema} onAction={onAction} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// HELPER FUNCTIONS
// ─────────────────────────────────────────────────────────────────────

function formatTaskType(type) {
  const labels = {
    general: 'Task',
    follow_up: 'Follow-up',
    call: 'Call',
    email: 'Email',
    meeting: 'Meeting',
    review: 'Review',
    proposal: 'Proposal',
    demo: 'Demo',
  };
  return labels[type] || type;
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);

  if (date.toDateString() === now.toDateString()) return 'Today';
  if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow';

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function SpendTable({ title, data }) {
  console.log('[SpendTable] Rendering with:', { title, data });
  const rows = data?.rows || data?.flows || [];

  if (rows.length === 0) {
    console.log('[SpendTable] No rows found in data');
    return (
      <div style={{ padding: 'var(--space-3)', color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>
        No spend data available
      </div>
    );
  }
  console.log('[SpendTable] Rendering', rows.length, 'rows');

  return (
    <div className="cc-table-container">
      {title && <div className="cc-table-title">{title}</div>}
      <div className="cc-table-scroll">
        <table className="cc-table">
          <thead>
            <tr>
              <th>Vendor</th>
              <th>Category</th>
              <th style={{ textAlign: 'right' }}>Items</th>
              <th style={{ textAlign: 'right' }}>Spend</th>
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 20).map((row, idx) => {
              const vendorDrill = row.drill || (row.vendor && {
                nodeType: 'vendor_name',
                nodeValue: row.vendor,
                department: row.department,
              });
              return (
                <tr key={idx}>
                  <td style={{ maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <DrillableValue drill={vendorDrill}>{row.vendor}</DrillableValue>
                  </td>
                  <td>
                    <DrillableValue drill={row.category && { nodeType: 'category', nodeValue: row.category, department: row.department }}>
                      {row.category}
                    </DrillableValue>
                  </td>
                  <td style={{ textAlign: 'right' }}>{(row.item_count || row.itemCount)?.toLocaleString()}</td>
                  <td style={{ textAlign: 'right', fontWeight: 'var(--font-medium)' }}>
                    <DrillableValue drill={vendorDrill}>
                      {formatSpend(row.total_spend || row.totalSpend)}
                    </DrillableValue>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {(rows.length > 20 || data?.total_flows > rows.length) && (
          <div style={{ padding: 'var(--space-2)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', textAlign: 'center' }}>
            Showing {Math.min(rows.length, 20)} of {data?.total_flows || rows.length} flows
          </div>
        )}
      </div>
    </div>
  );
}

function SpendSankey({ title, data }) {
  console.log('[SpendSankey] Rendering with:', { title, data });
  const flows = data?.flows || [];

  if (flows.length === 0) {
    console.log('[SpendSankey] No flows found in data');
    return (
      <div style={{ padding: 'var(--space-3)', color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>
        No flow data available
      </div>
    );
  }
  console.log('[SpendSankey] Rendering', flows.length, 'flows');

  // Group by category for a simple visualization.
  // Postgres numeric columns arrive as STRINGS — coerce before any math, or
  // += concatenates and the bar widths become NaN% (invisible bars).
  const byCategory = {};
  let totalSpend = 0;
  for (const f of flows) {
    const cat = f.category || 'Other';
    const spend = Number(f.total_spend ?? f.totalSpend) || 0;
    totalSpend += spend;
    if (!byCategory[cat]) byCategory[cat] = 0;
    byCategory[cat] += spend;
  }

  // Top 10 categories + an explicit "Other" bucket so the hidden tail is
  // visible instead of silently omitted from the flow
  const allSorted = Object.entries(byCategory).sort((a, b) => b[1] - a[1]);
  const sorted = allSorted.slice(0, 10);
  const tail = allSorted.slice(10);
  if (tail.length > 0) {
    const tailSum = tail.reduce((s, [, v]) => s + v, 0);
    sorted.push([`Other (${tail.length} categories)`, tailSum]);
  }

  return (
    <div style={{ padding: 'var(--space-2)' }}>
      {title && <div className="cc-section-title">{title}</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
        {sorted.map(([category, spend], idx) => {
          const pct = totalSpend > 0 ? (spend / totalSpend) * 100 : 0;
          return (
            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
              <div style={{ width: '100px', fontSize: 'var(--text-xs)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {category}
              </div>
              <div style={{ flex: 1, height: '16px', background: 'var(--color-surface-hover)', borderRadius: 'var(--radius-sm)', overflow: 'hidden' }}>
                <div style={{
                  width: `${pct}%`,
                  height: '100%',
                  background: `hsl(${210 - idx * 15}, 70%, 50%)`,
                  borderRadius: 'var(--radius-sm)',
                  transition: 'width 0.3s ease',
                }} />
              </div>
              <div style={{ width: '70px', fontSize: 'var(--text-xs)', textAlign: 'right', fontWeight: 'var(--font-medium)' }}>
                {formatSpend(spend)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MethodsChart({ title, data }) {
  const methods = data.methods || [];

  if (methods.length === 0) {
    return null;
  }

  const total = methods.reduce((sum, m) => sum + (Number(m.count) || 0), 0);

  return (
    <div style={{ padding: 'var(--space-2)' }}>
      {title && <div className="cc-section-title">{title}</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
        {methods.map((method, idx) => {
          const pct = total > 0 ? ((Number(method.count) || 0) / total) * 100 : 0;
          return (
            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
              <div style={{ width: '80px', fontSize: 'var(--text-xs)', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {method.name}
              </div>
              <div style={{ flex: 1, height: '12px', background: 'var(--color-surface-hover)', borderRadius: 'var(--radius-sm)', overflow: 'hidden' }}>
                <div style={{
                  width: `${pct}%`,
                  height: '100%',
                  background: 'var(--color-primary)',
                  borderRadius: 'var(--radius-sm)',
                }} />
              </div>
              <div style={{ width: '50px', fontSize: 'var(--text-xs)', textAlign: 'right' }}>
                {method.count?.toLocaleString()}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default ChatComponentRenderer;
